import { NavLink, Outlet, Link } from "react-router-dom";
import { Package as Pkg, Building2, MessageSquareQuote, Inbox, LogOut, ArrowUpRight } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const items = [
  { to: "/admin", end: true, label: "Overview", icon: Inbox },
  { to: "/admin/products", label: "Products", icon: Pkg },
  { to: "/admin/clients", label: "Clients", icon: Building2 },
  { to: "/admin/testimonials", label: "Testimonials", icon: MessageSquareQuote },
  { to: "/admin/contacts", label: "Contact Inquiries", icon: Inbox },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();

  return (
    <div data-testid="admin-layout" className="min-h-screen bg-slate-50 flex">
      <aside className="hidden md:flex w-64 bg-white border-r border-slate-200 flex-col">
        <div className="px-6 py-6 border-b border-slate-200">
          <Link to="/admin" className="flex items-center gap-2.5">
            <img
              src="/logo-icon-transparent.png"
              alt="High Roller"
              className="h-10 w-auto"
            />
            <div>
              <div className="font-heading font-semibold text-slate-900 text-sm">High Roller</div>
              <div className="text-[10px] uppercase tracking-widest text-emerald-600 font-semibold">Admin</div>
            </div>
          </Link>
        </div>

        <nav className="flex-1 px-3 py-5 space-y-1">
          {items.map(({ to, end, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={`admin-nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                  isActive ? "bg-blue-50 text-blue-700 font-medium" : "text-slate-600 hover:bg-slate-100"
                }`
              }
            >
              <Icon size={17} />
              {label}
            </NavLink>
          ))}
        </nav>

        <div className="px-4 py-4 border-t border-slate-200 space-y-2">
          <a
            href="/"
            target="_blank"
            rel="noreferrer"
            data-testid="admin-view-site"
            className="flex items-center justify-between px-3 py-2 rounded-lg text-xs text-slate-600 hover:bg-slate-100"
          >
            <span>View public site</span>
            <ArrowUpRight size={14} />
          </a>
          <div className="px-3 pt-2 border-t border-slate-100">
            <div className="text-xs text-slate-400 mb-1">Signed in as</div>
            <div className="text-sm font-medium text-slate-900 truncate" data-testid="admin-user-email">{user?.email}</div>
          </div>
          <button
            onClick={logout}
            data-testid="admin-logout"
            className="w-full flex items-center justify-center gap-2 text-sm text-slate-600 hover:text-red-600 hover:bg-red-50 px-3 py-2 rounded-lg transition-colors"
          >
            <LogOut size={15} /> Sign out
          </button>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        {/* Mobile top bar */}
        <div className="md:hidden flex items-center justify-between px-5 py-4 bg-white border-b border-slate-200">
          <Link to="/admin" className="flex items-center gap-2">
            <img
              src="/logo-icon-transparent.png"
              alt="High Roller"
              className="h-8 w-auto"
            />
            <span className="font-heading font-semibold text-sm">Admin</span>
          </Link>
          <button onClick={logout} className="text-sm text-slate-500"><LogOut size={16} /></button>
        </div>
        {/* Mobile nav */}
        <div className="md:hidden overflow-x-auto hide-scrollbar bg-white border-b border-slate-200">
          <div className="flex gap-1 px-3 py-2 w-max">
            {items.map(({ to, end, label, icon: Icon }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                className={({ isActive }) =>
                  `flex items-center gap-2 whitespace-nowrap px-3 py-2 rounded-lg text-xs ${
                    isActive ? "bg-blue-50 text-blue-700 font-medium" : "text-slate-600"
                  }`
                }
              >
                <Icon size={14} /> {label}
              </NavLink>
            ))}
          </div>
        </div>

        <main className="flex-1 p-6 md:p-10 overflow-x-hidden">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
